import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Smartphone, Info } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { Logo } from '@/components/Shell';
import { Button, Field, Input } from '@/components/ui';

/**
 * Tenants log in with their phone number and a 6-digit SMS code. No password
 * to set up or forget — the number is already on file with the landlord.
 */
export default function TenantLogin() {
  const { requestTenantCode, verifyTenantCode } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [smsConfigured, setSmsConfigured] = useState(true);

  async function onRequest(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await requestTenantCode(phone.trim());
      setSmsConfigured(res.smsConfigured);
      setStep('code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send a code');
    } finally {
      setLoading(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await verifyTenantCode(phone.trim(), code.trim());
      navigate('/portal');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not verify that code');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-bg mesh">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 text-sm text-muted mb-8 hover:text-ink w-fit">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="flex items-center gap-2.5 mb-8">
          <Logo />
          <span className="font-extrabold tracking-tight text-lg">PROVIA</span>
        </div>

        {step === 'phone' ? (
          <>
            <h1 className="text-title">Tenant portal</h1>
            <p className="text-muted mt-2 text-sm">
              Enter the phone number your landlord has on file. We will text you a code.
            </p>

            <form onSubmit={onRequest} className="mt-8 space-y-4">
              <Field label="Phone number" hint="Include the country code, e.g. +254712345678">
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+254712345678"
                  autoComplete="tel"
                  required
                  autoFocus
                />
              </Field>

              {error && (
                <div className="flex items-start gap-2 text-sm text-danger bg-danger/10
                                border border-danger/25 rounded-xl px-3 py-2.5">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                loading={loading}
                icon={<Smartphone className="h-4 w-4" />}
              >
                Send me a code
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="text-title">Enter your code</h1>
            <p className="text-muted mt-2 text-sm">
              We sent a 6-digit code to <span className="text-ink font-medium">{phone}</span>.
            </p>

            {!smsConfigured && (
              <div className="mt-4 flex items-start gap-2 text-sm bg-info/10 border border-info/25
                              rounded-xl px-3 py-2.5 text-info">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  SMS is not configured on this deployment, so no text was sent.
                  The code is printed in the server function logs.
                </span>
              </div>
            )}

            <form onSubmit={onVerify} className="mt-8 space-y-4">
              <Field label="6-digit code">
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-[0.5em] font-bold h-14 tnum"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                />
              </Field>

              {error && (
                <div className="flex items-start gap-2 text-sm text-danger bg-danger/10
                                border border-danger/25 rounded-xl px-3 py-2.5">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                loading={loading}
                disabled={code.length !== 6}
              >
                Sign in
              </Button>

              <button
                type="button"
                onClick={() => { setStep('phone'); setCode(''); setError(null); }}
                className="w-full text-sm text-muted hover:text-ink py-2"
              >
                Use a different number
              </button>
            </form>
          </>
        )}

        <p className="text-sm text-faint mt-8 text-center">
          Managing properties? <Link to="/login" className="link">Manager sign in</Link>
        </p>
      </div>
    </div>
  );
}
